import { useState } from "react";
import Sidebar from "../components/Sidebar";
import { paymentsHeader } from "../data/tableData";
import Dropdown from "../components/Dropdown";
import useDataFetch from "../hooks/useDataFetch";
import OrdersTable from "../components/OrdersTable";
import Pagination from "../components/Pagination";
import { MobileResponsive } from "./MobileResponsive";
import DynamicMetaTags from "../components/DynamicMetaTags";

const PAGE_SIZE = 15;

const timeOptions = [
  { label: "Last 3 days", value: "3" },
  { label: "Last 7 days", value: "7" },
  { label: "Last 30 days", value: "30" },
  { label: "Last 90 days", value: "90" },
  { label: "All time", value: "all" },
];

const Orders = () => {
  // Committed filter state — only updates on search submit
  const [filters, setFilters] = useState({
    orderId: "",
    outletName: "",
    driverName: "",
    time: "all",
  });

  // Draft inputs — updates as user types
  const [draft, setDraft] = useState({
    orderId: "",
    outletName: "",
    driverName: "",
  });

  const [selectedTimeLabel, setSelectedTimeLabel] = useState("All time");
  const [currentPage, setCurrentPage] = useState(1);

  const buildApiUrl = () => {
    const params = new URLSearchParams();
    params.append("page", currentPage);
    if (filters.orderId) params.append("orderId", filters.orderId);
    if (filters.outletName) params.append("outletName", filters.outletName);
    if (filters.driverName) params.append("driverName", filters.driverName);
    params.append("time", filters.time);
    return `/admin/payments?${params.toString()}`;
  };

  const { data, isLoading } = useDataFetch(buildApiUrl());

  const orders = data?.orders || [];
  const totalOrders = data?.total || 0;

  const handleDraftChange = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleTimeChange = (label) => {
    const value = timeOptions.find((t) => t.label === label)?.value || "3";
    setSelectedTimeLabel(label);
    setFilters((prev) => ({ ...prev, time: value }));
    setCurrentPage(1);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setFilters((prev) => ({
      ...prev,
      orderId: draft.orderId.trim(),
      outletName: draft.outletName.trim(),
      driverName: draft.driverName.trim(),
    }));
    setCurrentPage(1);
  };

  const handleClear = () => {
    setDraft({ orderId: "", outletName: "", driverName: "" });
    setFilters((prev) => ({ ...prev, orderId: "", outletName: "", driverName: "" }));
    setCurrentPage(1);
  };

  const hasActiveSearch = filters.orderId || filters.outletName || filters.driverName;

  return (
    <>
      <DynamicMetaTags
        pageTitle="Beep - Manage Payments & Order Transactions"
        pageDescription="Stay on top of your finances with Beep! Access detailed payment history for every order, ensuring transparency and accuracy in your transactions."
        pageKeywords="Beep, payment history, order payments, financial insights, transaction tracking"
      />
      <MobileResponsive />
      <div className="lg:flex hidden items-start">
        <Sidebar />

        <div className="ml-[14.5vw] w-full h-screen overflow-y-auto px-10 py-8 text-secondary">
          {/* Header row */}
          <div className="flex items-center justify-between mb-6">
            <h1 className="font-semibold text-[20px]">Total Orders</h1>
            <Dropdown
              options={timeOptions.map((t) => t.label)}
              selected={selectedTimeLabel}
              onSelect={handleTimeChange}
              label="Time range"
            />
          </div>

          {/* Filter bar */}
          <form onSubmit={handleSearch} className="flex items-center gap-3 mb-6">
            <input
              type="text"
              placeholder="Order ID"
              value={draft.orderId}
              onChange={(e) => handleDraftChange("orderId", e.target.value)}
              className="border-2 border-[#F7631D] rounded-full px-4 py-1.5 text-sm text-secondary outline-none focus:ring-2 focus:ring-[#F7631D]/30 w-44 placeholder:text-gray-400"
            />
            <input
              type="text"
              placeholder="Outlet name"
              value={draft.outletName}
              onChange={(e) => handleDraftChange("outletName", e.target.value)}
              className="border-2 border-[#F7631D] rounded-full px-4 py-1.5 text-sm text-secondary outline-none focus:ring-2 focus:ring-[#F7631D]/30 w-44 placeholder:text-gray-400"
            />
            <input
              type="text"
              placeholder="Driver name"
              value={draft.driverName}
              onChange={(e) => handleDraftChange("driverName", e.target.value)}
              className="border-2 border-[#F7631D] rounded-full px-4 py-1.5 text-sm text-secondary outline-none focus:ring-2 focus:ring-[#F7631D]/30 w-44 placeholder:text-gray-400"
            />
            <button
              type="submit"
              className="bg-[#F7631D] text-white px-5 py-1.5 rounded-full text-sm font-semibold hover:bg-[#d55419] transition-colors"
            >
              Search
            </button>
            {hasActiveSearch && (
              <button
                type="button"
                onClick={handleClear}
                className="border-2 border-gray-300 text-gray-500 px-5 py-1.5 rounded-full text-sm font-semibold hover:border-gray-400 transition-colors"
              >
                Clear
              </button>
            )}
          </form>

          {/* Results info */}
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-gray-500">
              {isLoading ? "Loading..." : `${totalOrders} order${totalOrders !== 1 ? "s" : ""} found`}
            </p>
          </div>

          {/* Table */}
          <div className="flex flex-col gap-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="h-10 w-10 rounded-full border-4 border-t-quaternary border-b-quaternary border-r-transparent border-l-transparent animate-spin"></div>
              </div>
            ) : (
              <OrdersTable headers={paymentsHeader} data={orders} />
            )}

            <div className="flex justify-center">
              <Pagination
                className="pagination-bar"
                currentPage={currentPage}
                totalCount={totalOrders}
                pageSize={PAGE_SIZE}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Orders;
